import React, { useEffect, useMemo, useRef } from 'react'
import { Box, Typography } from '@mui/material'
import { keyframes } from '@emotion/react'
import { gsap } from 'gsap'
import { INTERAX_COLORS, INTERAX_TYPOGRAPHY, FONT_FAMILY_PRIMARY } from '../../../../constants/colors'
import { useTranslation } from '@/shared/hooks/useIntractionTranslate'

export interface TeamHealthHeartVisualizationProps {
  /**
   * Team health value (0-1 normalized)
   */
  value: number
  /**
   * Optional title
   */
  title?: string
  /**
   * Optional description
   */
  description?: string
  /**
   * Variant for team, individual, or website styling
   */
  variant?: 'team' | 'individual' | 'website'
  /**
   * Size: default (full) or compact (e.g. for mobile carousel)
   */
  size?: 'default' | 'compact'
}

const HEART_WIDTH = 160
const HEART_HEIGHT = 140
const DEFAULT_WAVE_HEIGHT = 4
const DEFAULT_WAVE_OVERLAP = -39
const DEFAULT_ANIMATION_DURATION = 0.85
const FILL_OFFSET = -10

const waveDrift = keyframes`
  0% {
    transform: translateX(-90px);
  }
  100% {
    transform: translateX(85px);
  }
`

const generateClipPathId = () => `heart-clip-${Math.random().toString(36).slice(2, 9)}`

const heartPath =
  'M 80,30 C 80,30 63,12 46,12 C 28,12 17,24 17,42 C 17,66 39,90 80,128 C 121,90 143,66 143,42 C 143,24 132,12 114,12 C 97,12 80,30 80,30 Z'

const wavePath = 'M-160 44c30 0 58-18 88-18s58 18 88 18 58-18 88-18 58 18 88 18v44H-160z'

const COMPACT_SCALE = 0.7
const COMPACT_HEART_WIDTH = Math.round(HEART_WIDTH * COMPACT_SCALE)
const COMPACT_HEART_HEIGHT = Math.round(HEART_HEIGHT * COMPACT_SCALE)

const TeamHealthHeartVisualization: React.FC<TeamHealthHeartVisualizationProps> = ({
  value,
  title,
  description: _description,
  variant,
  size = 'default'
}) => {
  const isCompact = size === 'compact'
  const { t } = useTranslation()

  // Convert to percentage and clamp
  const percentage = Math.max(0, Math.min(100, Math.round(value * 100)))

  const normalized = useMemo(() => {
    const adjusted = percentage + FILL_OFFSET
    return Math.min(1, adjusted / 100)
  }, [percentage])

  const clipPathIdRef = useRef(generateClipPathId())
  const svgRef = useRef<SVGSVGElement | null>(null)
  const fillRectRef = useRef<SVGRectElement | null>(null)
  const waveGroupRef = useRef<SVGGElement | null>(null)
  const initializationRef = useRef(false)

  const fillHeight = useMemo(() => HEART_HEIGHT * normalized, [normalized])
  const fillY = useMemo(() => HEART_HEIGHT - fillHeight, [fillHeight])
  const waveY = useMemo(() => fillY - (DEFAULT_WAVE_HEIGHT - DEFAULT_WAVE_OVERLAP), [fillY])
  const fillRectY = useMemo(() => fillY - DEFAULT_WAVE_HEIGHT, [fillY])
  const fillRectHeight = useMemo(() => fillHeight + DEFAULT_WAVE_HEIGHT, [fillHeight])
  const safeFillRectHeight = useMemo(() => Math.max(fillRectHeight, 0.01), [fillRectHeight])

  useEffect(() => {
    if (!fillRectRef.current || !waveGroupRef.current || !svgRef.current) {
      return
    }

    if (!initializationRef.current) {
      gsap.set(fillRectRef.current, {
        attr: {
          y: fillRectY,
          height: safeFillRectHeight
        }
      })

      gsap.set(waveGroupRef.current, {
        y: waveY
      })

      initializationRef.current = true
      return
    }

    const timeline = gsap.timeline({
      defaults: { ease: 'power2.out', duration: DEFAULT_ANIMATION_DURATION }
    })

    timeline
      .to(
        fillRectRef.current,
        {
          attr: {
            y: fillRectY,
            height: safeFillRectHeight
          }
        },
        0
      )
      .to(
        waveGroupRef.current,
        {
          y: waveY
        },
        0
      )
      .fromTo(
        svgRef.current,
        { scale: 1 },
        {
          scale: 1.03,
          duration: 0.3,
          yoyo: true,
          repeat: 1,
          ease: 'power1.inOut'
        },
        0
      )

    return () => {
      timeline.kill()
    }
  }, [fillRectY, safeFillRectHeight, waveY])

  return (
    <Box
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: isCompact ? '8px' : '24px',
        ...(variant === 'website' && { paddingTop: isCompact ? '8px' : '15px' }),
        gap: isCompact ? '8px' : '16px',
        isolation: 'isolate',
        position: 'relative',
        width: '100%',
        height: variant === 'website' ? '100%' : '308px',
        background: `radial-gradient(50.19% 50.19% at 50% 50%, ${INTERAX_COLORS.white20} 0%, rgba(255, 255, 255, 0) 100%), rgba(33, 38, 41, 0.4)`,
        backgroundBlendMode: 'soft-light, normal',
        border: `1px solid ${INTERAX_COLORS.white04}`,
        boxShadow: 'inset 0px 1px 0px rgba(255, 255, 255, 0.08)',
        borderRadius: '10px',
        overflow: 'hidden'
      }}
    >
      {/* Orange glow ellipse - top left */}
      <Box
        sx={{
          position: 'absolute',
          width: '244px',
          height: '177px',
          left: '-120px',
          top: '-80px',
          background: INTERAX_COLORS.sun,
          filter: 'blur(75px)',
          zIndex: 0
        }}
      />

      {/* Title */}
      <Typography
        sx={
          variant === 'website'
            ? {
                height: isCompact ? '16px' : '24px',
                alignSelf: 'flex-start',
                fontFamily: FONT_FAMILY_PRIMARY,
                fontStyle: 'normal',
                fontWeight: 600,
                fontSize: isCompact ? '12px' : '16px',
                lineHeight: isCompact ? '16px' : '24px',
                color: '#FFFFFF',
                flex: 'none',
                order: 0,
                flexGrow: 0,
                zIndex: 1
              }
            : {
                ...INTERAX_TYPOGRAPHY.headline5,
                color: INTERAX_COLORS.white,
                zIndex: 1,
                alignSelf: 'stretch'
              }
        }
      >
        {title || t('gamer.feedback.teamHealth.title', 'Team Health')}
      </Typography>

      {/* Description - Hidden for website variant */}
      {variant !== 'website' && (
        <Typography
          sx={{
            ...INTERAX_TYPOGRAPHY.body3,
            color: INTERAX_COLORS.thunderGrey,
            zIndex: 1,
            alignSelf: 'stretch'
          }}
        >
          {_description || t('gamer.feedback.teamHealth.description', 'Your level of psychological safety as a team')}
        </Typography>
      )}

      {/* Heart SVG with Fill Animation */}
      <Box
        sx={{
          position: 'relative',
          width: isCompact ? `${COMPACT_HEART_WIDTH}px` : `${HEART_WIDTH}px`,
          height: isCompact ? `${COMPACT_HEART_HEIGHT + 10}px` : `${HEART_HEIGHT + 10}px`,
          zIndex: 2
        }}
      >
        <Box
          component="div"
          sx={{
            position: 'absolute',
            inset: 0,
            filter: 'drop-shadow(0px 14px 22px rgba(255, 171, 64, 0.28))'
          }}
        />
        <Box
          component="svg"
          ref={svgRef}
          viewBox="0 0 160 140"
          role="progressbar"
          aria-valuenow={percentage}
          aria-valuemin={0}
          aria-valuemax={100}
          sx={{
            width: isCompact ? `${COMPACT_HEART_WIDTH}px` : `${HEART_WIDTH}px`,
            height: isCompact ? `${COMPACT_HEART_HEIGHT}px` : `${HEART_HEIGHT}px`,
            display: 'block',
            overflow: 'visible',
            '& .wave use': {
              animation: `${waveDrift} 2.4s cubic-bezier(.55,.5,.45,.5) infinite` as const
            },
            '& .wave use:nth-of-type(1)': {
              animationDuration: '3s'
            },
            '& .wave use:nth-of-type(2)': {
              animationDuration: '4s'
            },
            '& .wave use:nth-of-type(3)': {
              animationDuration: '2.4s'
            }
          }}
        >
          <defs>
            <clipPath id={clipPathIdRef.current} clipPathUnits="userSpaceOnUse">
              <path d={heartPath} />
            </clipPath>
            <path id={`${clipPathIdRef.current}-wave`} d={wavePath} />
          </defs>

          <path d={heartPath} fill="rgba(40, 45, 48, 0.6)" />

          <g clipPath={`url(#${clipPathIdRef.current})`}>
            <rect ref={fillRectRef} width={HEART_WIDTH} fill={INTERAX_COLORS.sun} />

            <g ref={waveGroupRef}>
              <g className="wave">
                <use href={`#${clipPathIdRef.current}-wave`} fill="rgba(255, 200, 128, 0.6)" />
                <use href={`#${clipPathIdRef.current}-wave`} fill="rgba(255, 200, 128, 0.45)" />
                <use href={`#${clipPathIdRef.current}-wave`} fill="rgba(255, 171, 64, 0.8)" />
              </g>
            </g>
          </g>

          <path d={heartPath} fill="transparent" stroke="rgba(192, 192, 192, 0.75)" strokeWidth={5} />
        </Box>

        {/* Percentage Text */}
        <Typography
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 10,
            fontFamily: 'Lexend Deca',
            fontWeight: 600,
            fontSize: isCompact ? '20px' : '28px',
            lineHeight: isCompact ? '26px' : '36px',
            color: INTERAX_COLORS.white,
            textShadow: '0px 0px 10px rgba(0, 0, 0, 0.8), 0px 2px 4px rgba(0, 0, 0, 0.5)'
          }}
        >
          {percentage}%
        </Typography>
      </Box>
    </Box>
  )
}

export default TeamHealthHeartVisualization
